import React, { useMemo, useState } from 'react';
import type { Theme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type * as MonacoEditor from 'monaco-editor/esm/vs/editor/editor.api';
import {
  deleteInputBindingFromCode,
  reanchorInputBindingsToSource,
  saveButtonBindings,
  TREE_SITTER_NOT_READY_REASON,
  type SaveButtonBindingsResult,
} from '../controllerCodeUtils';
import ButtonMappingDialog from '../ButtonMappingDialog';
import { translateControllerInputButton, translateDrivebaseInputButton } from '../inputButtonI18n';
import { useAppDispatch, useAppSelector } from '../../../../redux/ReduxHooks';
import { applicationSlice } from '../../../../redux/ApplicationSlice';
import { setInputBindingList } from '../../../../redux/EditorSlice';
import { useResolvedMappingsReady } from '../../../../hooks/editor/usePythonTreeSitterReady';
import { useLogging } from '../../../../logging/logging';
import type { FrameworkArgument } from '../../../../framework/framework';
import type { InputBinding, InputBindingAction, InputBindingDevice, InputBindingTableRow } from '../../../../utils/inputBindings';
import {
  canDeleteInputBindingFromCode,
  dedupeInputBindingsBySignature,
  getInputBindingRowLabel,
  getUnsupportedInputBindingActionExpression,
  hasUnsupportedInputBindingCode,
  INPUT_BINDING_DEVICE,
  INPUT_BINDING_KIND,
  inputBindingsToTableRows,
  isUnsupportedInputBinding,
} from '../../../../utils/inputBindings';
import { isSequenceTupleArg, isTupleArg, readTupleMemberValue } from '../../../../utils/tupleArg';
import ListHeader from './ListHeader';
import ListRow, { PROTECTED_FILL_COLUMN_TEMPLATE } from './ListRow';
import MappingListGrid from './MappingListGrid';
import { iconButtonCell } from './listCellUtils';

const { DRIVEBASE } = INPUT_BINDING_DEVICE;

export interface InputMappingControlListProps {
  device: InputBindingDevice;
  editor?: MonacoEditor.editor.IStandaloneCodeEditor;
  /**
   * When provided, gates mutations from the parent (e.g. Control tab readiness banner).
   * When omitted, falls back to live Tree-sitter readiness so controls cannot stay enabled
   * while WASM is loading or after init failed.
   */
  mappingsReady?: boolean;
}

const InputMappingControlList: React.FC<InputMappingControlListProps> = ({ device, editor, mappingsReady }) => {
  const { t } = useTranslation();
  const logger = useLogging();
  const globalInputBindingList = useAppSelector((state) => state.editor.inputBindingList);
  const dispatch = useAppDispatch();
  const hoveredBtn = useAppSelector((state) => state.application.hoveredControllerButton);
  const [newDialogOpen, setNewDialogOpen] = useState<boolean>(false);
  const [dialogButton, setDialogButton] = useState<string | undefined>(undefined);
  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const ready = useResolvedMappingsReady(mappingsReady);
  const mutationsDisabled = readOnly || !ready;
  const mappingsUnavailableDescription = t(
    'controller.buttonMapping.description.mappingsUnavailable',
    'Input mapping tools are still loading or unavailable. Retry from the banner above, then try again.'
  );
  const addInputActionDescription = ready
    ? t('controller.buttonMapping.description.addInputAction', 'add input action')
    : mappingsUnavailableDescription;
  const editInputActionDescription = ready
    ? t('controller.buttonMapping.description.editInputAction', 'edit input action')
    : mappingsUnavailableDescription;
  const deleteInputActionDescription = ready
    ? t('controller.buttonMapping.description.deleteInputAction', 'delete input action')
    : mappingsUnavailableDescription;

  const deviceBindings = useMemo(
    () => sortBindings(dedupeInputBindingsBySignature(globalInputBindingList.filter((binding: InputBinding) => binding.device === device))),
    [globalInputBindingList, device]
  );
  const rows = useMemo(() => inputBindingsToTableRows(deviceBindings), [deviceBindings]);

  function displayActionName(row: InputBindingTableRow) {
    const unsupportedActionExpression = getUnsupportedInputBindingActionExpression(row.action);
    if (unsupportedActionExpression) {
      const prefix = t('controller.buttonMapping.unsupportedAction', 'Unsupported custom code');
      return `${prefix}: ${unsupportedActionExpression}`;
    }

    if (isUnsupportedInputBinding(row.binding)) {
      const rawExpression = String(row.binding.rawActionExpression || row.binding.rawCode || '').trim();
      const prefix = t('controller.buttonMapping.unsupportedAction', 'Unsupported custom code');
      return rawExpression ? `${prefix}: ${rawExpression}` : prefix;
    }

    const bindingAction: InputBindingAction = row.action;
    const action = bindingAction.action;
    const actionName = String(action?.name || '');
    if (actionName.trim() === '') {
      return t('controller.buttonMapping.none', 'None');
    }
    if (String(bindingAction.parentName || '').toLowerCase() === 'custom') {
      return actionName;
    }

    const pathParts: string[] = [];
    const parent = String(bindingAction.parentName || '').trim();
    if (parent) pathParts.push(parent);
    if (bindingAction.mechanismName) pathParts.push(bindingAction.mechanismName);

    const args = action?.arguments || [];
    let actionSegment: string;
    if (args.length > 0) {
      const allHaveValues = args.every((a: FrameworkArgument) => {
        const v = a?.value;
        return !(v === undefined || v === null || v === '');
      });
      const asValueString = (v: unknown, arg?: FrameworkArgument): string => {
        if (typeof v === 'boolean') return v ? 'true' : 'false';
        if (typeof v === 'number') return String(v);
        if (typeof v === 'string') {
          if (/^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*)*$/.test(v)) return v;
          return v;
        }
        if (Array.isArray(v)) {
          return `(${v.map((item) => asValueString(item)).join(', ')})`;
        }
        if (v && typeof v === 'object') {
          if (arg && isTupleArg(arg) && arg.members?.length) {
            const parts = arg.members.map((member, index) => {
              const memberValue = readTupleMemberValue(arg, index, member.name);
              return asValueString(memberValue ?? (v as Record<string, unknown>)[member.name]);
            });
            return `(${parts.join(', ')})`;
          }
          if (arg && isSequenceTupleArg(arg)) {
            // Unsupported / raw sequence expressions are stored as strings; object rows are rare in list display.
            return String(arg.rawSourceExpression || v);
          }
          if (arg?.rawSourceExpression) return String(arg.rawSourceExpression);
          return `(${Object.values(v as Record<string, unknown>)
            .map((item) => asValueString(item))
            .join(', ')})`;
        }
        if (v === undefined || v === null) return '';
        return String(v);
      };
      const argStr = allHaveValues
        ? args.map((a: FrameworkArgument) => asValueString(a.value, a)).join(', ')
        : args
            .map((a: FrameworkArgument) => {
              const v = a?.value;
              if (v === undefined || v === null || v === '') return `${a.name}=`;
              return `${a.name}=${asValueString(v, a)}`;
            })
            .join(', ');
      actionSegment = `${actionName}(${argStr})`;
    } else {
      actionSegment = actionName;
    }

    const segments = [...pathParts, actionSegment];
    const deduped = segments.filter((segment, index) => index === 0 || segment !== segments[index - 1]);
    return deduped.join('.');
  }

  function sortBindings(list: InputBinding[]): InputBinding[] {
    const kindRank = (kind: InputBinding['kind']) =>
      kind === INPUT_BINDING_KIND.VARIABLE ? 0 : kind === INPUT_BINDING_KIND.PRESSED ? 1 : kind === INPUT_BINDING_KIND.RELEASED ? 2 : 3;
    const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
    return [...list].sort((a, b) => {
      const actionA = a.kind === INPUT_BINDING_KIND.TOGGLE ? a.onAction : a.action;
      const actionB = b.kind === INPUT_BINDING_KIND.TOGGLE ? b.onAction : b.action;
      const mechA = (actionA.mechanismName || '').toLowerCase();
      const mechB = (actionB.mechanismName || '').toLowerCase();
      const byMech = cmp(mechA, mechB);
      if (byMech !== 0) return byMech;
      const btnA = String(a.button || '').toLowerCase();
      const btnB = String(b.button || '').toLowerCase();
      const byBtn = cmp(btnA, btnB);
      if (byBtn !== 0) return byBtn;
      return kindRank(a.kind) - kindRank(b.kind);
    });
  }

  const commitBindingListAfterDelete = (nextDeviceBindings: InputBinding[]) => {
    const others = globalInputBindingList.filter((binding: InputBinding) => binding.device !== device);
    // Delete shifts lines below the removed span; re-derive anchors once from the mutated source.
    dispatch(setInputBindingList(reanchorInputBindingsToSource(editor, [...others, ...nextDeviceBindings])));
  };

  const handleDelete = (row: InputBindingTableRow) => {
    if (mutationsDisabled) return;
    if (!canDeleteInputBindingFromCode(row.binding)) return;
    if (isUnsupportedInputBinding(row.binding) && !editor) return;
    if (editor) {
      const result = deleteInputBindingFromCode(editor, row.binding);
      // Only remove the Control row after Main.py actually changes. `changed: false` can mean the
      // mapping is already gone *or* still present but unmatched (unexpected formatting).
      // TODO(BPS-800 follow-up): confirm absence vs unmatched so stale Redux rows can clear safely.
      if (!result.ok) {
        logger.error(result.reason);
        return;
      }
      if (!result.changed) {
        // Soft no-op: call may already be gone, or still present but unmatched. Do not error —
        // absence is a normal race with hand-edits / stale Redux until BPS-800 can distinguish.
        logger.log('Input mapping delete made no source change; leaving the Control row in place.');
        return;
      }
    }
    commitBindingListAfterDelete(deviceBindings.filter((binding) => binding.id !== row.bindingId));
  };

  const handleOpenNew = () => {
    if (mutationsDisabled) return;
    setDialogButton(undefined);
    setNewDialogOpen(true);
  };

  const handleOpenEdit = (row: InputBindingTableRow) => {
    if (mutationsDisabled) return;
    if (hasUnsupportedInputBindingCode(row.binding)) return;
    setDialogButton(String(row.button || ''));
    setNewDialogOpen(true);
  };

  const handleCloseNew = () => {
    setNewDialogOpen(false);
    setDialogButton(undefined);
  };

  const handleSaveButtonBindings = (button: string, nextBindings: InputBinding[]): SaveButtonBindingsResult => {
    if (!ready) return { ok: false, reason: TREE_SITTER_NOT_READY_REASON.replace };
    const result = saveButtonBindings(editor, globalInputBindingList, device, button, nextBindings);
    // Failures are returned to ButtonMappingDialog, which shows saveError — do not also logger.error.
    if (!result.ok) return result;
    // saveButtonBindings already re-anchored the full list from the mutated source — do not
    // rebuild from stale Redux peers and re-parse again.
    dispatch(setInputBindingList(result.nextBindingList));
    return result;
  };

  const gridTemplateColumns = `minmax(0, max-content) minmax(0, max-content) ${PROTECTED_FILL_COLUMN_TEMPLATE} 2.5rem 2.5rem`;

  return (
    <MappingListGrid
      scrollTestId={`controller-button-mapping-${device}-scroll`}
      gridTestId={`controller-button-mapping-${device}-grid`}
      gridTemplateColumns={gridTemplateColumns}
    >
      <ListHeader
        columns={[
          { columnWidth: 'fill', label: t('controller.buttonMapping.header.button', 'Button'), align: 'center' },
          { columnWidth: 'fill', label: t('controller.buttonMapping.header.trigger', 'Trigger'), align: 'center' },
          { columnWidth: 'protectedFill', label: t('controller.buttonMapping.header.action', 'Action'), align: 'left' },
        ]}
        addAriaLabel={t('controller.buttonMapping.aria.newMapping', 'New mapping')}
        addDescription={addInputActionDescription}
        addDescriptionId={`controller-button-mapping-${device}-add-description`}
        onAdd={handleOpenNew}
        addDisabled={mutationsDisabled}
        AddIcon={AddIcon}
        trailingActionColumnCount={2}
        useParentColumns
      />

      {rows.map((row) => {
        const actionText = displayActionName(row) || '';

        return (
          <React.Fragment key={row.id}>
            <ListRow
              useParentColumns
              cells={[
                {
                  columnWidth: 'fill',
                  align: 'center',
                  content:
                    device === DRIVEBASE
                      ? translateDrivebaseInputButton(String(row.button), t)
                      : translateControllerInputButton(String(row.button), t),
                  fontWeight: hoveredBtn && hoveredBtn === String(row.button).toLowerCase() ? 700 : 400,
                },
                {
                  columnWidth: 'fill',
                  align: 'center',
                  content: getInputBindingRowLabel(row, t),
                  fontWeight: hoveredBtn && hoveredBtn === String(row.button).toLowerCase() ? 600 : 400,
                },
                {
                  columnWidth: 'protectedFill',
                  align: 'left',
                  content: actionText,
                  fontWeight: hoveredBtn && hoveredBtn === String(row.button).toLowerCase() ? 600 : 400,
                  noWrap: false,
                },
                iconButtonCell({
                  ariaLabel: t('controller.buttonMapping.aria.editMapping', 'Edit mapping'),
                  description: editInputActionDescription,
                  descriptionId: `controller-button-mapping-${device}-${row.id}-edit-description`,
                  onClick: () => handleOpenEdit(row),
                  Icon: EditIcon,
                  disabled: mutationsDisabled || hasUnsupportedInputBindingCode(row.binding),
                }),
                iconButtonCell({
                  ariaLabel: t('controller.buttonMapping.aria.deleteMapping', 'Delete mapping'),
                  description: deleteInputActionDescription,
                  descriptionId: `controller-button-mapping-${device}-${row.id}-delete-description`,
                  onClick: () => handleDelete(row),
                  Icon: DeleteIcon,
                  disabled: mutationsDisabled || !canDeleteInputBindingFromCode(row.binding) || (isUnsupportedInputBinding(row.binding) && !editor),
                }),
              ]}
              rowSx={(theme: Theme) => ({
                px: 2,
                py: 0.5,
                bgcolor: hoveredBtn && hoveredBtn === String(row.button) ? theme.vars?.palette?.accent?.opaque : 'transparent',
                borderLeft:
                  hoveredBtn && hoveredBtn === String(row.button) ? `3px solid ${theme.vars?.palette?.accent?.main}` : '3px solid transparent',
                transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
              })}
              onMouseEnter={() => dispatch(applicationSlice.actions.onHoverControllerButton(String(row.button)))}
              onMouseLeave={() => dispatch(applicationSlice.actions.onHoverControllerButton(null))}
            />
          </React.Fragment>
        );
      })}
      <ButtonMappingDialog
        open={newDialogOpen}
        device={device}
        onClose={handleCloseNew}
        onSave={handleSaveButtonBindings}
        initialButton={dialogButton}
        mappingsReady={ready}
      />
    </MappingListGrid>
  );
};

export default InputMappingControlList;
